import React, { useEffect, useState } from "react";
import GetAppIcon from "@mui/icons-material/GetApp";
import UpdateIcon from "@mui/icons-material/Update";
import CloseIcon from "@mui/icons-material/Close";

const PWAPrompts = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [showUpdatePrompt, setShowUpdatePrompt] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState(null);

  useEffect(() => {
    // 1. Check if app is running in standalone mode (already installed)
    const isStandalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;

    if (isStandalone) {
      return;
    }

    // 2. Listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      const hasShown = sessionStorage.getItem("pwa_prompt_shown");
      if (!hasShown) {
        setShowInstallPrompt(true);
        sessionStorage.setItem("pwa_prompt_shown", "true");
      }
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

    // 3. Listen for SW update event from index.js
    const handleSWUpdate = (e) => {
      console.log("[SW Update] New service worker version detected.");
      // e.detail is the SW registration object
      if (e.detail && e.detail.waiting) {
        setWaitingWorker(e.detail);
        setShowUpdatePrompt(true);
      }
    };

    window.addEventListener("sw-update-available", handleSWUpdate);

    // 4. Check for existing waiting service worker on mount
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.getRegistration().then((reg) => {
        if (reg && reg.waiting) {
          console.log("[SW Update] Existing waiting worker detected on mount.");
          setWaitingWorker(reg);
          setShowUpdatePrompt(true);
        }
      });

      // Listen for controllerchange to reload the page when new worker takes over
      const handleControllerChange = () => {
        window.location.reload();
      };
      navigator.serviceWorker.addEventListener("controllerchange", handleControllerChange);

      return () => {
        window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
        window.removeEventListener("sw-update-available", handleSWUpdate);
        navigator.serviceWorker.removeEventListener("controllerchange", handleControllerChange);
      };
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.removeEventListener("sw-update-available", handleSWUpdate);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    console.log(`[PWA Install] User choice: ${outcome}`);
    setDeferredPrompt(null);
    setShowInstallPrompt(false);
  };

  const handleDismissInstall = () => {
    setShowInstallPrompt(false);
  };

  const handleUpdateClick = () => {
    if (waitingWorker && waitingWorker.waiting) {
      console.log("[SW Update] Skipping waiting and activating new worker...");
      waitingWorker.waiting.postMessage({ type: "SKIP_WAITING" });
    }
    setShowUpdatePrompt(false);
  };

  const handleDismissUpdate = () => {
    setShowUpdatePrompt(false);
  };

  return (
    <>
      {/* 1. Install Prompt Banner */}
      {showInstallPrompt && deferredPrompt && (
        <div style={styles.promptContainer}>
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <div style={styles.iconContainer}>
                <img src="/logo192.png" alt="Corporate HR" style={styles.appIcon} />
              </div>
              <div style={styles.textContainer}>
                <h4 style={styles.title}>Install Corporate HR</h4>
                <p style={styles.description}>
                  Install this app on your device for a faster, full-screen, and offline-ready experience.
                </p>
              </div>
              <button onClick={handleDismissInstall} style={styles.closeBtn}>
                <CloseIcon sx={{ fontSize: 18 }} />
              </button>
            </div>
            <div style={styles.actionContainer}>
              <button onClick={handleDismissInstall} style={styles.secondaryBtn}>
                Later
              </button>
              <button onClick={handleInstallClick} style={styles.primaryBtn}>
                <GetAppIcon sx={{ fontSize: 18 }} /> Install
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 2. Update Prompt Banner */}
      {showUpdatePrompt && (
        <div style={styles.promptContainer}>
          <div style={{ ...styles.card, borderColor: "var(--primary-main)" }}>
            <div style={styles.cardHeader}>
              <div style={{ ...styles.iconContainer, backgroundColor: "rgba(25, 118, 210, 0.1)" }}>
                <UpdateIcon sx={{ color: "var(--primary-main)", fontSize: 24 }} />
              </div>
              <div style={styles.textContainer}>
                <h4 style={styles.title}>Update Available</h4>
                <p style={styles.description}>
                  A new version of Corporate HR is ready. Update now to get the latest features and improvements.
                </p>
              </div>
              <button onClick={handleDismissUpdate} style={styles.closeBtn}>
                <CloseIcon sx={{ fontSize: 18 }} />
              </button>
            </div>
            <div style={styles.actionContainer}>
              <button onClick={handleDismissUpdate} style={styles.secondaryBtn}>
                Later
              </button>
              <button onClick={handleUpdateClick} style={{ ...styles.primaryBtn, backgroundColor: "var(--primary-main)" }}>
                Update Now
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const styles = {
  promptContainer: {
    position: "fixed",
    bottom: "24px",
    left: "50%",
    transform: "translateX(-50%)",
    width: "90%",
    maxWidth: "420px",
    zIndex: 9999,
    fontFamily: "var(--font-family, 'Inter', sans-serif)",
    animation: "slideUp 0.3s ease-out forwards",
  },
  card: {
    backgroundColor: "rgba(255, 255, 255, 0.95)",
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    borderRadius: "16px",
    padding: "16px",
    boxShadow: "0 10px 25px rgba(0, 0, 0, 0.15)",
    border: "1px solid rgba(226, 232, 240, 0.8)",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  cardHeader: {
    display: "flex",
    alignItems: "flex-start",
    gap: "14px",
    position: "relative",
  },
  iconContainer: {
    width: "48px",
    height: "48px",
    borderRadius: "12px",
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#f8fafc",
    flexShrink: 0,
  },
  appIcon: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  textContainer: {
    flexGrow: 1,
    paddingRight: "24px",
  },
  title: {
    margin: "0 0 4px 0",
    fontSize: "15px",
    fontWeight: "700",
    color: "#0f172a",
  },
  description: {
    margin: 0,
    fontSize: "12px",
    lineHeight: "1.4",
    color: "#64748b",
  },
  closeBtn: {
    position: "absolute",
    top: "-4px",
    right: "-4px",
    background: "none",
    border: "none",
    color: "#94a3b8",
    cursor: "pointer",
    padding: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "50%",
    transition: "background-color 0.2s",
  },
  actionContainer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "10px",
  },
  secondaryBtn: {
    background: "none",
    border: "none",
    padding: "8px 16px",
    fontSize: "13px",
    fontWeight: "600",
    color: "#64748b",
    cursor: "pointer",
    borderRadius: "8px",
    transition: "background-color 0.2s",
  },
  primaryBtn: {
    backgroundColor: "#0ea5e9",
    border: "none",
    color: "#ffffff",
    padding: "8px 16px",
    fontSize: "13px",
    fontWeight: "600",
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    cursor: "pointer",
    borderRadius: "8px",
    boxShadow: "0 4px 6px -1px rgba(14, 165, 233, 0.2)",
    transition: "background-color 0.2s, transform 0.1s",
  },
};

export default PWAPrompts;
